import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import moment from 'moment';
import { compose, prop, pick, assoc, over, lensProp, findIndex, propEq, remove, trim } from 'ramda';

import { Formik, Form } from 'formik';
import { Grid, Collapse, Switch, FormControlLabel, Typography, TextField, Chip, IconButton, InputAdornment } from '@material-ui/core';
import ModalDrawerSwitcher from '../ModalDrawerSwitcher';
import Loader from '../Loader';
import Select from '../Select';
import Input from '../Input';
import Button from '../Button';
import ExportPDF from '../ExportPDF';
import SingleDatePicker from '../SingleDatePicker';

import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';

import Close from '@material-ui/icons/Close';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload } from '@fortawesome/free-solid-svg-icons';

import {
  loadReceiptInvoicePrint,
  loadInvoiceDetails,
  upsertInvoice,
  payInvoice,
  undoPayment,
  emailInvoice,
  changeInvoiceSituation
} from '../../pages/ClientInvoices/store/thunk';

import {
  clearForm,
  handlePaying,
  handleEmailing,
  handleCanceling,
  handleCancelingPayment
} from '../../pages/ClientInvoices/store/actions';

import {
  convertToOptions,
  convertAmountToDB,
  convertInvoicePrint,
  convertToEmptyString,
  convertDateToDB
} from '../../helpers/converters';

import { loadAllPaymentMethods } from 'pages/PaymentMethods/store/thunk';
import { setRpsInfo } from 'pages/ProvisionalReceipt/store/actions';
import { loadNfseConcept } from 'pages/ProvisionalReceipt/store/thunk';
import { sendNfse } from 'pages/ProvisionalReceiptConfig/store/thunk';

import useWindowDimensions from 'helpers/hooks/useWindowDimensions';

import { validations } from './validations';

export default function Invoice({ isOpen, setIsOpenInvoice, pageOrigin, establishmentId, clientsPage, transactionsQp, transactionsPage, transactionsPageSize }) {
  const dispatch = useDispatch();

  const { width } = useWindowDimensions();
  const isSm = width < 960;

  const { userId } = useSelector(state => state.profile);
  const { activePage: page, size: pageSize } = useSelector(state => state.pagination);
  const { selects, qp } = useSelector(state => state.filters);
  const { startDate, endDate } = useSelector(state => state.dateFilter);
  const clientId = useSelector(state => state.clientRegistration.basicInfo.clientId);
  const clientName = useSelector(state => state.clientRegistration.basicInfo.submit.name);
  const situationEstablishments = useSelector(state => state.login.situation.establishments);
  const { penalty, penaltyType } = useSelector(state => state.clientRegistration.charges.submit);
  const { isSendNfseLoading } = useSelector(state => state.provisionalReceiptConfig);
  const { option } = useSelector(state => state.modalDrawerSwitcher);

  const isModal = option == 'modal';

  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([
    {
      label: '--',
      value: ''
    }
  ]);

  const [isSavingInvoice, setIsSavingInvoice] = useState(false);

  const accounts = convertToOptions(['account'], selects);
  const findCashier = findIndex(propEq('label', 'Caixa'));
  const availableAccountsFilter = compose(remove(findCashier, 1), remove(0, 1));
  const availableAccounts = availableAccountsFilter(accounts);

  const {
    isDetailing,
    isUpdatingInvoice,
    isPaying,
    isEmailing,
    isCanceling,
    isCancelingPayment,
    invoiceDateTime,
    submit,
    submit: {
      situationId,
      invoiceSituation,
      paymentDateTime,
      dueDateTime,
      userOutputName,
      accountId,
      transactionId
    }
  } = useSelector(state => state.clientInvoices);

  const {
    isNfInfo,
    nfInfo: {
      rpsSeries,
      rpsNumber,
      nfNumber,
      nfStatus
    } = {}
  } = useSelector(state => state.provisionalReceipt);

  const taxSettings = situationEstablishments
    ?.find(establishment => +establishment?.establishmentId == +submit?.establishmentId)
    ?.nfseTaxSetting;

  const [dueDateTimeWhenPaying, setDueDateTimeWhenPaying] = useState('');

  const handleLoadInvoiceDetails = () => {
    if(pageOrigin == 'transactions') {
      return dispatch(loadInvoiceDetails(userId, establishmentId, clientId, invoiceDateTime?.toString()?.slice(-14)));
    }

    return dispatch(loadInvoiceDetails(userId, establishmentId, clientId, invoiceDateTime));
  }

  useEffect(() => {
    handleLoadInvoiceDetails();

    dispatch(loadAllPaymentMethods(userId, establishmentId)).then(data => {
      const paymentMethods = data?.map(item => {
        return {
          label: item?.paymentMethodName,
          value: item?.establishmentPaymentMethodId
        }
      });

      setAvailablePaymentMethods([
        ...availablePaymentMethods,
        ...paymentMethods
      ]);
    });
  }, []);

  useEffect(() => {
    dispatch(setRpsInfo({
      rpsSeries: '',
      rpsNumber: '',
      nfNumber: '',
      nfStatus: ''
    }));

    const { clientId, invoiceDateTime } = submit || {};
    const id = `${clientId}${invoiceDateTime}`;

    if(+taxSettings == 1 && !!transactionId && (+situationId == 2 || +situationId == 3)) {
      if(id) {
        dispatch(loadNfseConcept(userId, establishmentId, id));
      }
    }
  }, [taxSettings, transactionId, situationId]);

  const handlePrintInvoice = () => {
    const params = {
      userId,
      establishmentId,
      clientId,
      invoiceDateTime: invoiceDateTime?.toString()?.slice(-14)
    }

    return dispatch(loadReceiptInvoicePrint(params));
  }

  const handleModal = () => {
    setIsOpenInvoice(false);
    dispatch(clearForm());
  }

  const togglePaymentOptions = () => {
    dispatch(handlePaying());
  }

  const toggleSendInvoice = () => {
    dispatch(handleEmailing());
  }

  const handleSendInvoice = ({ email }) => {
    return dispatch(emailInvoice(userId, establishmentId, clientId, invoiceDateTime?.toString()?.slice(-14), { email }));
  }

  const toggleCancelInvoice = () => {
    dispatch(handleCanceling());
  }

  const toggleCancelPayment = () => {
    dispatch(handleCancelingPayment());
  }

  const handleCancelInvoice = ({ userPassword, reason }) => {
    const params = {
      userPassword,
      reason,
      situationId
    }

    const properties = {
      page: pageOrigin == 'transactions' ? transactionsPage : page,
      pageSize: pageOrigin == 'transactions' ? transactionsPageSize : pageSize,
      startDate,
      endDate,
      pageOrigin,
      qp: pageOrigin == 'transactions' ? transactionsQp : qp
    }

    return dispatch(changeInvoiceSituation(userId, establishmentId, clientId, invoiceDateTime, params, properties)).then(() => {
      setIsOpenInvoice(false);
    });
  }

  const handlePayment = (
    {
      amount,
      amountReceived,
      paymentDateTime,
      paymentMethod,
      dueDateTime,
      currentAmount,
    },
    { resetForm }
  ) => {
    const handleAmountToSend = amount =>
      amountReceived
        ? convertAmountToDB(amountReceived)
        : convertAmountToDB(amount);

    if(!moment(paymentDateTime).isValid()) {
      return toast.error('Selecione uma data de pagamento válida.');
    }

    const formattedPaymentDateTime =
      paymentDateTime == '0001-01-01' ||
      paymentDateTime == '0001-01-01 00:00:00' ||
      paymentDateTime == 'Data inválida' ||
      paymentDateTime == 'Invalid date'
        ? moment().format('YYYY-MM-DD')
        : paymentDateTime;

    const params = isPaying
      ? {
          amount: handleAmountToSend(handleAmountToSend(amount)),
          paymentDateTime: `${moment(formattedPaymentDateTime, 'YYYY-MM-DD').format('YYYY-MM-DD')} ${moment().format('HH:mm:ss')}`,
          paymentMethod
        }
      : {
          situationId,
          amount: convertAmountToDB(currentAmount),
          dueDateTime: `${moment(dueDateTime, 'YYYY-MM-DD').format('YYYY-MM-DD')} ${moment().format('HH:mm:ss')}`
        }

    const properties = {
      userId,
      establishmentId,
      clientListEstablishmentId: establishmentId,
      clientId,
      invoiceDateTime: invoiceDateTime?.toString()?.slice(-14),
      page: pageOrigin == 'clients' ? clientsPage : page,
      pageSize,
      startDate,
      endDate,
      pageOrigin,
      qp
    }

    if(isPaying) {
      return dispatch(payInvoice(params, properties)).then(error => {
        if(!error) {
          resetForm();
          setIsOpenInvoice(false);
        }
      });
    } else {
      return dispatch(upsertInvoice(params, properties));
    }
  }

  const handleCancelPayment = ({ reason, userPassword }, { resetForm }) => {
    const params = {
      situationId: 1,
      reason,
      userPassword
    }

    const properties = {
      userId,
      establishmentId,
      clientId,
      invoiceDateTime: invoiceDateTime?.toString()?.slice(-14),
      page: pageOrigin == 'transactions' ? transactionsPage : page,
      pageSize: pageOrigin == 'transactions' ? transactionsPageSize : pageSize,
      startDate,
      endDate,
      pageOrigin,
      qp: pageOrigin == 'transactions' ? transactionsQp : qp
    }

    return dispatch(undoPayment(params, properties)).then(error => {
      if(!error) {
        resetForm();
      }
    });
  }

  const handleSubmit = async (values, actions) => {
    if(!isEmailing && !isCanceling && !isCancelingPayment) {
      setIsSavingInvoice(true);

      try {
        return await handlePayment(values, actions);
      } finally {
        setIsSavingInvoice(false);
      }
    }

    if(isEmailing) {
      return handleSendInvoice(values, actions);
    }

    if(isCancelingPayment) {
      return handleCancelPayment(values, actions);
    }

    return handleCancelInvoice(values, actions);
  }

  const handleSaveButton = () => {
    if(isPaying) {
      return 'Pagar fatura';
    }

    if(invoiceSituation === 'Paga') {
      return 'Desfazer pagamento';
    }

    return 'Salvar';
  }

  const getChipStyle = () => {
    if(invoiceSituation === 'Cancelada') {
      return {
        color: '#F44336',
        backgroundColor: 'rgba(244, 67, 54, 0.2)'
      }
    }

    if(invoiceSituation === 'Paga') {
      return {
        color: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.2)'
      }
    }

    return {
      color: '#FF9800',
      backgroundColor: 'rgba(255, 152, 0, 0.2)'
    }
  }

  const getPenaltyAndValueSize = () => {
    if(isSm) {
      return 12;
    }

    if(isModal && !!transactionId) {
      return 3;
    }

    return 6;
  }

  const labelStatus = () => {
    return(
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <Typography variant='body1'>
          Detalhamento da fatura
        </Typography>
        <Chip
          label={invoiceSituation}
          style={getChipStyle()}
        />
      </div>
    );
  }

  const initialValues = compose(
    over(lensProp('paymentMethod'), convertToEmptyString),
    over(lensProp('email'), trim),
    assoc('isPaying', isPaying),
    assoc('isEmailing', isEmailing),
    assoc('isCanceling', isCanceling),
    assoc('isCancelingPayment', isCancelingPayment),
    pick([
      'dueDateTime',
      'currentDueDateTime',
      'paymentMethod',
      'paymentDateTime',
      'amount',
      'amountReceived',
      'currentAmount',
      'email',
      'reason',
      'userPassword',
      'clientName'
    ]),
    prop('submit')
  );

  const invoicePaid = invoiceSituation == 'Paga' || invoiceSituation == 'Cancelada';

  return(
    <>
      <ModalDrawerSwitcher
        id="accounts"
        title={labelStatus()}
        open={isOpen}
        onClose={handleModal}
      >
        <Formik
          enableReinitialize
          initialValues={initialValues(useSelector(state => state.clientInvoices))}
          validationSchema={validations}
          onSubmit={handleSubmit}
        >
          {({ ...formikProps }) => (
            <Form style={{ height: '100%' }}>
              <Loader isLoading={isDetailing || isUpdatingInvoice || isSavingInvoice}>
                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%' }}>
                  <div>
                    <Grid container spacing={2}>
                      <Grid item xs={(isModal && !isSm) ? 6 : 12}>
                        <Input
                          disabled
                          id="clientName"
                          name="clientName"
                          label="Cliente"
                          value={clientName || formikProps.values.clientName}
                        />
                      </Grid>
                      <Grid item xs={(isModal && !isSm) ? 6 : 12}>
                        {formikProps.values.isPaying ? (
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                              disabled
                              inputFormat="DD/MM/YYYY"
                              label="Data do vencimento"
                              value={dueDateTimeWhenPaying}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  name="dueDateTime"
                                  variant='outlined'
                                  inputProps={{
                                    ...params.inputProps
                                  }}
                                  SelectProps={{
                                    MenuProps: {
                                      disableEnforceFocus: true
                                    }
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        ) : (
                          <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                              disabled={invoicePaid || formikProps.values.isPaying}
                              inputFormat="DD/MM/YYYY"
                              label="Data do vencimento"
                              value={formikProps.values.dueDateTime || dueDateTime}
                              onChange={date => formikProps.setFieldValue('dueDateTime', convertDateToDB(date))}
                              renderInput={params => (
                                <TextField
                                  {...params}
                                  fullWidth
                                  name="dueDateTime"
                                  variant='outlined'
                                  inputProps={{
                                    ...params.inputProps
                                  }}
                                  SelectProps={{
                                    MenuProps: {
                                      disableEnforceFocus: true
                                    }
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        )}
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      {!!transactionId && (
                        <Grid item xs={(isModal && !isSm) ? 6 : 12}>
                          <Select
                            disabled
                            id="account"
                            name="account"
                            label="Selecione a conta destino"
                            value={
                              formikProps.values.account ||
                              accountId ||
                              (availableAccounts.length > 0 ? availableAccounts[0].value : '')
                            }
                            options={accounts}
                            error={formikProps.errors.account}
                            helperText={formikProps.errors.account}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                      )}
                      <Grid item xs={getPenaltyAndValueSize()}>
                        <Input
                          disabled
                          id="penaltyType"
                          name="penaltyType"
                          label={penaltyType === 1 ? 'Multa (R$)' : 'Multa (%)'}
                          value={penalty}
                        />
                      </Grid>
                      <Grid item xs={getPenaltyAndValueSize()}>
                        <Input
                          id="currentAmount"
                          name="currentAmount"
                          label="Valor"
                          type="money"
                          disabled={isPaying || invoicePaid}
                          value={formikProps.values.currentAmount}
                          error={formikProps.errors.currentAmount}
                          helperText={formikProps.errors.currentAmount}
                          onChange={formikProps.handleChange}
                        />
                      </Grid>
                    </Grid>
                    <hr />
                    <Grid container style={{ marginBottom: 10 }}>
                      <Grid item xs={12}>
                        <FormControlLabel
                          id="invoiceSituation"
                          name="invoiceSituation"
                          label="Recebido"
                          control={
                            <Switch
                              color="primary"
                              disabled={invoicePaid}
                              onChange={() => {
                                setDueDateTimeWhenPaying(formikProps.initialValues.dueDateTime);
                                return togglePaymentOptions();
                              }}
                              checked={formikProps.values.isPaying || invoiceSituation === 'Paga'}
                            />
                          }
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}
                      >
                        {situationId === 2 && (
                          <Grid item>
                            <Typography>Recebido por {userOutputName} ás {moment(paymentDateTime).format('DD/MM/YYYY HH:mm')}</Typography>
                          </Grid>
                        )}
                        {(invoiceSituation === 'Paga' && rpsNumber > 0) && (
                          <Grid item>
                            <Typography>RPS: {rpsSeries}-{rpsNumber} | N˚ NFSe: {nfNumber}</Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    <Collapse in={isPaying || invoiceSituation === 'Paga'}>
                      <Grid container spacing={2}>
                        <Grid item xs={(isModal && !isSm) ? 3 : 12}>
                          <SingleDatePicker
                            name="paymentDateTime"
                            label="Data do recebimento"
                            disabled={invoicePaid}
                            initialDate={
                              moment(formikProps.values.paymentDateTime).format('DD/MM/YYYY') != '01/01/0001' &&
                              moment(paymentDateTime).format('DD/MM/YYYY') != '01/01/0001'
                                ? (formikProps.values.paymentDateTime || paymentDateTime)
                                : moment().format('YYYY-MM-DD')
                            }
                            onDateChange={date => formikProps.setFieldValue('paymentDateTime', convertDateToDB(date))}
                          />
                        </Grid>
                        <Grid item xs={(isModal && !isSm) ? 6 : 12}>
                          <Select
                            id="paymentMethod"
                            name="paymentMethod"
                            label="Selecione o meio de pagamento"
                            disabled={invoicePaid}
                            options={availablePaymentMethods}
                            error={isCanceling ? null : formikProps.errors.paymentMethod}
                            helperText={formikProps.errors.paymentMethod}
                            value={formikProps.values.paymentMethod}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                        <Grid item xs={(isModal && !isSm) ? 3 : 12}>
                          <Input
                            id="amountReceived"
                            name="amountReceived"
                            type="money"
                            label="Valor recebido"
                            placeholder="R$ 0,00"
                            disabled={invoicePaid}
                            value={formikProps.values.amountReceived}
                            error={formikProps.errors.amountReceived}
                            helperText={formikProps.errors.amountReceived}
                            onChange={formikProps.handleChange}
                          />
                        </Grid>
                      </Grid>
                      <Grid container spacing={1}>
                        {isSm && (
                          <Grid item xs={4}>
                            <ExportPDF
                              fullWidth
                              fontSize={8}
                              type="button"
                              exportType="receiptInvoice"
                              color="tertiary"
                              variant="outlined"
                              fileName="fatura"
                              disabled={!situationId}
                              loading={isDetailing}
                              formatComponentData={convertInvoicePrint}
                              requestData={handlePrintInvoice}
                              startIcon={
                                <FontAwesomeIcon
                                  size='xs'
                                  icon={faDownload}
                                />
                              }
                            >
                              Baixar fatura
                            </ExportPDF>
                          </Grid>
                        )}
                        {isSm && (
                          <Grid item xs={4}>
                            <Button
                              fullWidth
                              fontSize={8}
                              color="tertiary"
                              variant="outlined"
                              disabled={!situationId}
                              loading={isDetailing}
                              onClick={toggleSendInvoice}
                            >
                              Enviar fatura
                            </Button>
                          </Grid>
                        )}
                        {isSm && (
                          <Grid item xs={4}>
                            {((+taxSettings == 1) && !!transactionId) && (
                              <>
                                {(!isNfInfo && nfStatus != 2) && (
                                  <Button
                                    color="success"
                                    type="button"
                                    loading={isSendNfseLoading}
                                    onClick={() => {
                                      const { establishmentId, clientId, invoiceDateTime } = submit || {};
                                      dispatch(sendNfse(userId, establishmentId, `${clientId}${invoiceDateTime}`, { resend: 1 }));
                                    }}
                                  >
                                    Enviar NFS-e
                                  </Button>
                                )}
                              </>
                            )}
                          </Grid>
                        )}
                      </Grid>
                    </Collapse>
                  </div>
                  <div style={{ marginBottom: 5 }}>
                    <Grid container style={{ display: 'flex', justifyContent: isModal ? 'flex-end' : 'flex-start', gap: 10 }}>
                      <Grid item xs={12}>
                        <Collapse in={isEmailing}>
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <TextField
                                fullWidth
                                variant="outlined"
                                id="email"
                                name="email"
                                label="E-mail"
                                value={formikProps.values.email}
                                error={formikProps.errors.email}
                                helperText={formikProps.errors.email}
                                onChange={e => formikProps.setFieldValue('email', e.target.value)}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton
                                        tabIndex="-1"
                                        onClick={() => formikProps.setFieldValue('email', '')}
                                      >
                                        <Close />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                              />
                            </Grid>
                          </Grid>
                        </Collapse>
                        <Collapse in={isCanceling}>
                          <Grid container spacing={2}>
                            <Grid item md={6} xs={12}>
                              <Input
                                id="reason"
                                name="reason"
                                label={
                                  situationId === 3
                                    ? 'Motivo da restauração'
                                    : 'Motivo do cancelamento'
                                }
                                value={formikProps.values.reason}
                                error={formikProps.errors.reason}
                                helperText={formikProps.errors.reason}
                                onChange={formikProps.handleChange}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Input
                                id="userPassword"
                                type="password"
                                label="Confirme sua senha"
                                error={formikProps.errors.userPassword}
                                helperText={formikProps.errors.userPassword}
                                onChange={formikProps.handleChange}
                              />
                            </Grid>
                          </Grid>
                        </Collapse>
                        <Collapse in={isCancelingPayment}>
                          <Grid container spacing={2}>
                            <Grid item md={6} xs={12}>
                              <Input
                                id="reason"
                                label="Motivo do cancelamento"
                                error={formikProps.errors.reason}
                                helperText={formikProps.errors.reason}
                                onChange={formikProps.handleChange}
                              />
                            </Grid>
                            <Grid item md={6} xs={12}>
                              <Input
                                id="userPassword"
                                type="password"
                                label="Confirme sua senha"
                                error={formikProps.errors.userPassword}
                                helperText={formikProps.errors.userPassword}
                                onChange={formikProps.handleChange}
                              />
                            </Grid>
                          </Grid>
                        </Collapse>
                      </Grid>
                      {isEmailing && (
                        <Grid item style={{ display: 'flex', gap: 10 }}>
                          <Button
                            color="error"
                            variant="outlined"
                            loading={isDetailing}
                            onClick={toggleSendInvoice}
                          >
                            Cancelar
                          </Button>
                          <Button
                            color="primary"
                            loading={isDetailing}
                          >
                            Enviar fatura
                          </Button>
                        </Grid>
                      )}
                      {isCanceling && (
                        <Grid item style={{ display: 'flex', gap: 10 }}>
                          <Button
                            color="error"
                            variant="outlined"
                            loading={isDetailing}
                            onClick={toggleCancelInvoice}
                          >
                            Cancelar
                          </Button>
                          <Button
                            color="primary"
                            className="mr-2"
                            loading={isDetailing}
                          >
                            Confirmar
                          </Button>
                        </Grid>
                      )}
                      {isCancelingPayment && (
                        <Grid item style={{ display: 'flex', justifyContent: 'flex-start', gap: 10 }}>
                          <Button
                            color="error"
                            variant="outlined"
                            loading={isDetailing}
                            onClick={toggleCancelPayment}
                          >
                            Cancelar
                          </Button>
                          <Button
                            type="submit"
                            color="primary"
                            className="mr-2"
                            loading={isDetailing}
                          >
                            Confirmar
                          </Button>
                        </Grid>
                      )}
                      {(!isEmailing && !isCanceling && !isCancelingPayment) && (
                        <Grid container spacing={1} justify='flex-end' alignItems='center'>
                          {!isSm && (
                            <Grid item>
                              <ExportPDF
                                type="button"
                                exportType="receiptInvoice"
                                color="tertiary"
                                variant="outlined"
                                fileName="fatura"
                                disabled={!situationId}
                                loading={isDetailing}
                                formatComponentData={convertInvoicePrint}
                                requestData={handlePrintInvoice}
                                startIcon={
                                  <FontAwesomeIcon
                                    size='xs'
                                    icon={faDownload}
                                  />
                                }
                              >
                                Baixar fatura
                              </ExportPDF>
                            </Grid>
                          )}
                          {!isSm && (
                            <Grid item>
                              <Button
                                color="tertiary"
                                variant="outlined"
                                disabled={!situationId}
                                loading={isDetailing}
                                onClick={toggleSendInvoice}
                              >
                                Enviar fatura
                              </Button>
                            </Grid>
                          )}
                          {situationId != 2 && (
                            <Grid item>
                              <Button
                                fullWidth
                                fontSize={isSm ? 8 : ''}
                                color='error'
                                variant='outlined'
                                loading={isDetailing}
                                onClick={toggleCancelInvoice}
                              >
                                {situationId == 3 ? 'Restaurar' : 'Cancelar'} fatura
                              </Button>
                            </Grid>
                          )}
                          <Grid item>
                            <Button
                              fullWidth
                              fontSize={isSm ? 8 : ''}
                              color={invoiceSituation === 'Paga' ? 'error' : 'success'}
                              variant={invoiceSituation === 'Paga' ? 'outlined' : 'contained'}
                              loading={isDetailing}
                              disabled={invoiceSituation === 'Cancelada'}
                              onClick={invoiceSituation === 'Paga' ? toggleCancelPayment : null}
                            >
                              {handleSaveButton()}
                            </Button>
                          </Grid>
                          {!isSm && (
                            <>
                              {((+taxSettings == 1) && !!transactionId) && (
                                <>
                                  {(!isNfInfo && nfStatus != 2) && (
                                    <Button
                                      color="success"
                                      type="button"
                                      loading={isSendNfseLoading}
                                      onClick={() => {
                                        const { establishmentId, clientId, invoiceDateTime } = submit || {};
                                        dispatch(sendNfse(userId, establishmentId, `${clientId}${invoiceDateTime}`, { resend: 1 }));
                                      }}
                                    >
                                      Enviar NFS-e
                                    </Button>
                                  )}
                                </>
                              )}
                            </>
                          )}
                        </Grid>
                      )}
                    </Grid>
                  </div>
                </div>
              </Loader>
            </Form>
          )}
        </Formik>
      </ModalDrawerSwitcher>
    </>
  );
}

Invoice.propTypes = {
  pageOrigin: PropTypes.string,
  isOpen: PropTypes.bool.isRequired
};

Invoice.defaultProps = {
  pageOrigin: 'invoices'
};